import { api } from '../../api/endpoints'
import type { ChannelSummary } from '../../api/types'
import { channelTables, db, deleteChannelData } from '../../db/db'
import { subscribeAdminChannel, unsubscribeAdminChannel } from '../../realtime/subscriptions'
import { useEphemeral } from '../../state/ephemeral'
import { summaryToRow, upsertUsers } from '../../sync/applyEvents'
import { syncAll } from '../../sync/syncEngine'

/**
 * Admin opening a channel they are not a member of: store a placeholder with version null so the
 * next sync returns a snapshot, subscribe to live events, then sync.
 */
export async function openAdminChannel(channelId: string, summary?: ChannelSummary): Promise<boolean> {
  let source = summary
  if (!source) {
    const detail = await api.channel(channelId)
    source = detail.channel
    await upsertUsers(detail.users)
    // The reason this reading is happening under, for the banner to say while it is open.
    if (detail.adminAccess) useEphemeral.getState().setAdminAccess(channelId, detail.adminAccess)
  }
  const existing = await db.channels.get(channelId)
  if (!existing) {
    await db.channels.put({ ...summaryToRow(source, undefined), version: null, headSeq: 0 })
  }
  subscribeAdminChannel(channelId)
  await syncAll()
  return Boolean(await db.channels.get(channelId))
}

/** Leaving an admin view drops the unsubscribed copy so it is not synced (and audited) again. */
export async function closeAdminChannel(channelId: string): Promise<void> {
  unsubscribeAdminChannel(channelId)
  await db.transaction('rw', channelTables(), async () => {
    const channel = await db.channels.get(channelId)
    if (channel && !channel.isMember) await deleteChannelData(channelId)
  })
}

const viewers = new Map<string, number>()
const pendingClose = new Map<string, ReturnType<typeof setTimeout>>()

/** A conversation screen is showing this channel (ref-counted, tolerant of quick remounts). */
export function retainChannelView(channelId: string): void {
  viewers.set(channelId, (viewers.get(channelId) ?? 0) + 1)
  const timer = pendingClose.get(channelId)
  if (timer) clearTimeout(timer)
  pendingClose.delete(channelId)
}

/** The screen went away: drop a non-member admin copy unless it is shown again shortly. */
export function releaseChannelView(channelId: string, delayMs = 1500): void {
  const count = Math.max(0, (viewers.get(channelId) ?? 1) - 1)
  viewers.set(channelId, count)
  if (count > 0) return
  const timer = setTimeout(() => {
    pendingClose.delete(channelId)
    if ((viewers.get(channelId) ?? 0) === 0) void closeAdminChannel(channelId)
  }, delayMs)
  pendingClose.set(channelId, timer)
}
